import React, { useContext, useEffect, useState } from "react";
import produitDataFetcher, { OffreAlimentaireInterface, OffreAlimentaireAnneesInterface } from "dataProvider/Produit";
import usePromise from "helpers/usePromise";
import Loader from "ui/Loader";
import BackToId from "ui/Button/BackToId";
import PagerNav from "ui/List/PagerNav";
import { IntlContext } from "context";
import Warnings from "../../Common/Warnings";
import { matomoPush } from "../../../Produit/helpers";

const ListAlimentaire: React.FC<{ activeFilters: any }> = ({ activeFilters }) => {
    const intl = useContext(IntlContext);
    const [downloading, setDownloading] = useState<boolean>(false);
    const activeFiltersJson = JSON.stringify(activeFilters);
    const { isLoading, changePromiseParams, data } = usePromise(produitDataFetcher.alimentaire, activeFilters);
    const filterFamily = activeFilters?.filter_famille?.length || 0;

    useEffect(() => {
        changePromiseParams(JSON.parse(activeFiltersJson));
    }, [activeFiltersJson, changePromiseParams]);

    if (isLoading || !data) {
        return <Loader message={intl.formatMessage("generic.loader.loading.label")} />;
    }

    const nbAnnees = Object.values(data[0]?.annees || [])?.length;

    const exportRows = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        try {
            event.preventDefault();
            setDownloading(true);
            matomoPush([["trackEvent", "Export outil de visualisation", "Offre alimentaire", JSON.stringify(activeFilters?.matomo), 1]]);

            produitDataFetcher
                .alimentaireExport(activeFilters)
                .then((data: any) => {
                    let file = window.URL.createObjectURL(data.content);
                    let tempLink = document.createElement("a");
                    tempLink.href = file;
                    tempLink.download = data.name;
                    tempLink.click();
                    tempLink.parentNode?.removeChild(tempLink);
                    window.URL.revokeObjectURL(file);
                })
                .catch((err) => {
                    console.error(err);
                })
                .finally(() => setDownloading(false));
        } catch (e) {
            console.error(e);
        }
    };

    const totalResults = (): string => {
        let total = data?.length || 0;

        if (typeof total !== "number") {
            return "";
        }
        if (total === 0) {
            return intl.formatMessage("produit.label.no_result");
        }
        return (
            `${total} ` +
            (total > 1 ? intl.formatMessage("offre_alimentaire.label.results") : intl.formatMessage("offre_alimentaire.label.result"))
        );
    };

    const goToPage = (page: number) => {
        changePromiseParams(
            Object.assign({}, data.active_filters, {
                page,
                order: data.pager.order,
                direction: data.pager.direction,
                per_page: data.pager.per_page,
            })
        );
    };

    let lastSecteur: string | null = null,
        dataFooter: HashMap<HashMap<OffreAlimentaireAnneesInterface>> = {};

    const sortAnnees = (a: OffreAlimentaireAnneesInterface, b: OffreAlimentaireAnneesInterface) => {
        const startYearA = parseInt(a.annee.toString());
        const startYearB = parseInt(b.annee.toString());
        return startYearA - startYearB;
    }

    const setLastSecteur = (secteur: string) => {
        lastSecteur = secteur;
    };

    const exportButton = () => {
        return <div className="container--export">
            <button
                type="button"
                className="cta cta--secondary"
                title={intl.formatMessage("generic.export")}
                disabled={downloading}
                onClick={(e) => exportRows(e)}
            >
                {downloading
                    ? intl.formatMessage("generic.loader.loading.label")
                    : intl.formatMessage("generic.export")}
            </button>
        </div>
    };

    if (!data || data.length <= 0) {
        return (
            <>
                <div className="stats-container-top">
                    <span className="results">{totalResults()}</span>
                </div>
                <Warnings warnings={data?.warnings} />
            </>
        );
    }

    const familyExist = () => {
        if (!data || data.length <= 0 || activeFilters?.filter_famille < 1) {
            return true;
        }

        const familles = data.filter((row: OffreAlimentaireInterface) => {
            return activeFilters?.filter_famille?.includes(String(row.famille_id));
        });

        return familles.length;
    }


    const getExplainMsgs = () => {
        let msgs = [];

        msgs.push(intl.formatMessage("produit.view_data.alimentaire.familles_comprises"));

        if (familyExist() === 0) {
            msgs.push(intl.formatMessage("produit.view_data.alimentaire.familles_comprises.sans_famille"));
        } else if (familyExist() === filterFamily && filterFamily > 0) {
            msgs.push(intl.formatMessage("produit.view_data.alimentaire.familles_comprises.sup"));
        } else if (filterFamily > familyExist()) {
            msgs.push(intl.formatMessage("produit.view_data.alimentaire.familles_comprises.famille_partielle"));
        }

        return <div>{msgs.map((msg:string, i:number) => <React.Fragment key={i}>{msg}<br/></React.Fragment>)}</div>
    }

    return (
        <>
            {exportButton()}
            <div className="stats-container-top">
                <span className="results">{totalResults()}</span>
            </div>
            {getExplainMsgs()}
            <Warnings warnings={data?.warnings} />

            <table className="stats-table-alimentaire">
                <thead className="row-group">
                    <tr className="header-row">
                        <th className="header-cell" rowSpan={2}>
                            {intl.formatMessage("produit.view_data.alimentaire.table.header.secteur")}
                        </th>
                        <th className="header-cell" rowSpan={2}>
                            {intl.formatMessage("produit.view_data.alimentaire.table.header.famille")}
                        </th>
                        <th className="header-cell" colSpan={nbAnnees}>
                            {intl.formatMessage("produit.view_data.alimentaire.table.header.repartition")}
                        </th>
                        <th className="header-cell" colSpan={nbAnnees}>
                            {intl.formatMessage("produit.view_data.alimentaire.table.header.nombre_ref")}
                        </th>
                    </tr>
                    <tr className="header-row">
                        {data &&
                            Object.values(data[0].annees).sort(sortAnnees).map((data: OffreAlimentaireAnneesInterface, keyA: number) => {
                                return (
                                    <th className="header-cell" key={`${keyA}-head-recap`}>
                                        {data.annee}
                                    </th>
                                );
                            })}
                        {data &&
                            Object.values(data[0].annees).sort(sortAnnees).map((data: OffreAlimentaireAnneesInterface, keyA: number) => {
                                return (
                                    <th className="header-cell" key={`${keyA}-head-prod`}>
                                        {data.annee}
                                    </th>
                                );
                            })}
                    </tr>
                </thead>
                <tbody>
                    {data.map(
                        (row: OffreAlimentaireInterface, key: number) => {
                            if (lastSecteur !== row.secteur) {
                                dataFooter = {};
                                Object.values(row.annees).map((annee: OffreAlimentaireAnneesInterface) => {
                                    if (dataFooter[row.secteur_id] === undefined) {
                                        dataFooter[row.secteur_id] = {};
                                    }
                                    if (dataFooter[row.secteur_id][annee.annee] === undefined) {
                                        dataFooter[row.secteur_id][annee.annee] = {
                                            annee: annee.annee,
                                            produits: 0,
                                            repartition: 0,
                                        };
                                    }
                                    return dataFooter;
                                }, dataFooter);
                            }

                            Object.values(row.annees).map(
                                (annee: OffreAlimentaireAnneesInterface) => {
                                    dataFooter[row.secteur_id][annee.annee].produits += annee.produits;
                                    dataFooter[row.secteur_id][annee.annee].repartition += annee.repartition;
                                    return dataFooter;
                                },
                                { dataFooter, row }
                            );

                            return (
                                <React.Fragment key={key}>
                                    <tr className={(!!filterFamily && activeFilters?.filter_famille?.includes("" + row.famille_id)) ? "row-light" : ""}>
                                        <td className="cell">{row.secteur}</td>
                                        <td className="cell">{row.famille}</td>
                                        {Object.values(row.annees).sort(sortAnnees).map(
                                            (annee: OffreAlimentaireAnneesInterface, keyA: number) => {
                                                if (annee.repartition === 0) {
                                                    return (
                                                        <td className="header-cell" key={`${keyA}-data-prod`}>
                                                            _
                                                        </td>
                                                    );
                                                }
                                                return (
                                                    <td className="header-cell" key={`${keyA}-data-rep`}>
                                                        {intl.formatNumber(annee.repartition, {
                                                            maximumFractionDigits: 2,
                                                            minimumFractionDigits: 2,
                                                            style: "percent",
                                                        })}
                                                    </td>
                                                );
                                            }
                                        )}
                                        {Object.values(row.annees).sort(sortAnnees).map(
                                            (annee: OffreAlimentaireAnneesInterface, keyA: number) => {
                                                if (annee.produits === 0) {
                                                    return (
                                                        <td className="header-cell" key={`${keyA}-data-prod`}>
                                                            _
                                                        </td>
                                                    );
                                                }
                                                return (
                                                    <td className="header-cell" key={`${keyA}-data-prod`}>
                                                        {intl.formatNumber(annee.produits, {
                                                            maximumFractionDigits: 0,
                                                            minimumFractionDigits: 0,
                                                        })}
                                                    </td>
                                                );
                                            }
                                        )}
                                    </tr>
                                    {row.secteur !== data[key + 1]?.secteur && (
                                        <>
                                            <tr key={`${key}-${row.secteur_id}`} className="tfoot">
                                                <td className="cell">{row.secteur}</td>
                                                <td className="cell">
                                                    {intl.formatMessage(
                                                        "produit.view_data.alimentaire.table.header.familles"
                                                    )}
                                                </td>
                                                {Object.values(dataFooter[row.secteur_id]).sort(sortAnnees).map(
                                                    (annee: OffreAlimentaireAnneesInterface, key: number) => {
                                                        if (annee.produits === 0) {
                                                            return (
                                                                <td
                                                                    className="header-cell"
                                                                    key={`${key}-footer-produits`}
                                                                >_</td>
                                                            );
                                                        }
                                                        return (
                                                            <td
                                                                className="header-cell"
                                                                key={`${key}-footer-recap`}
                                                            >
                                                                100 %
                                                            </td>
                                                        );
                                                    }
                                                )}
                                                {Object.values(dataFooter[row.secteur_id]).sort(sortAnnees).map(
                                                    (annee: OffreAlimentaireAnneesInterface, key: number) => {
                                                        if (annee.produits === 0) {
                                                            return (
                                                                <td
                                                                    className="header-cell"
                                                                    key={`${key}-footer-produits`}
                                                                >_</td>
                                                            );
                                                        }
                                                        return (
                                                            <td
                                                                className="header-cell"
                                                                key={`${key}-footer-produits`}
                                                            >
                                                                {intl.formatNumber(annee.produits, {
                                                                    maximumFractionDigits: 0,
                                                                    minimumFractionDigits: 0,
                                                                })}
                                                            </td>
                                                        );
                                                    }
                                                )}
                                            </tr>
                                        </>
                                    )}
                                    {setLastSecteur(row.secteur)}
                                </React.Fragment>
                            );
                        },
                        { lastSecteur, dataFooter }
                    )}
                </tbody>
            </table>

            {/* -- pagination */}
            <PagerNav
                currentPage={data?.pager?.current_page}
                lastPage={data?.pager?.last_page}
                onChange={goToPage}
            />
            {exportButton()}

            <BackToId label={intl.formatMessage("generic.go_to.top")} toId="pageup"/>
        </>
    );
};

export default ListAlimentaire;
